// Edge Diagnostics helpers (owner request 2026-10-14): pure formatting and classification for pages/EdgeDiagnostics.jsx, kept free of React so
// `node --test frontend/tests/edgeDiagnostics.test.mjs` can check them. Binds to GET /api/edge/diagnostics (shared/edge_diagnostics.py).
// Rule: a missing number is shown as "n/a", never as 0, and a failed section shows its error instead of an old value (a wrong all-clear is the failure to avoid).

export const HORIZONS = [['1m', '1 min'], ['5m', '5 min'], ['15m', '15 min'], ['eod', 'End of day'], ['next_open', 'Next open']]
export const DAYS = [1, 5, 20]

/** Signed percent with a fixed number of decimals; null/undefined/NaN -> 'n/a'. The input is already a percent (0.12 means 0.12 %). */
export function fmtPct(v, digits = 3) {
  if (v === null || v === undefined || Number.isNaN(Number(v))) return 'n/a'
  const n = Number(v)
  return `${n > 0 ? '+' : ''}${n.toFixed(digits)}%`
}

/** Plain share (0..1) as a percent without a sign; null -> 'n/a'. */
export function fmtShare(v, digits = 1) {
  if (v === null || v === undefined || Number.isNaN(Number(v))) return 'n/a'
  return `${(Number(v) * 100).toFixed(digits)}%`
}

export function fmtSec(v) {
  if (v === null || v === undefined || Number.isNaN(Number(v))) return 'n/a'
  return `${Number(v).toFixed(2)} s`
}

/** Cluster interval text: '[-0.213, -0.044] over 31 symbol-days' or 'n/a'. */
export function fmtInterval(stats) {
  if (!stats || stats.lo === null || stats.lo === undefined) return stats && stats.clusters ? `${stats.clusters} symbol-days (too few for an interval)` : 'n/a'
  return `[${Number(stats.lo).toFixed(3)}, ${Number(stats.hi).toFixed(3)}] over ${stats.clusters} symbol-days`
}

const GATE_STYLE = {
  helping: { label: 'HELPING', cls: 'bg-emerald-950 text-emerald-300 border-emerald-700', hint: 'what it blocked lost money after cost' },
  hurting: { label: 'HURTING', cls: 'bg-rose-950 text-rose-300 border-rose-700', hint: 'what it blocked made money after cost' },
  neutral: { label: 'NEUTRAL', cls: 'bg-slate-800 text-slate-300 border-slate-600', hint: 'interval spans zero' },
  gross_positive: { label: 'UP BEFORE COST', cls: 'bg-amber-950 text-amber-300 border-amber-700', hint: 'blocked signals gained before cost; the spread is unknown, so this cannot be called hurting yet' },
  insufficient: { label: 'TOO FEW', cls: 'bg-slate-900 text-slate-500 border-slate-700', hint: 'not enough symbol-days to judge' },
}
export function gateStyle(verdict) {
  return GATE_STYLE[verdict] || GATE_STYLE.insufficient
}

/** Tone of a signed value for text colour: red below zero, green above, grey when missing or exactly zero. */
export function signTone(v) {
  if (v === null || v === undefined || Number.isNaN(Number(v)) || Number(v) === 0) return 'text-slate-400'
  return Number(v) < 0 ? 'text-rose-400' : 'text-emerald-400'
}

/** Names of the sections that came back with an error, for the banner. */
export function failedSections(payload) {
  const sec = (payload && payload.sections) || {}
  return Object.keys(sec).filter(k => sec[k] && sec[k].error).map(k => ({ name: k, error: sec[k].error }))
}

/** Net effect of the gates in plain words, or null when the net comparison was withheld (server says `net_comparable` false) or either side has no net figure (never guessed). */
export function gatesSentence(tvb) {
  if (!tvb || tvb.net_comparable === false || tvb.net_effect_of_gates_pct === null || tvb.net_effect_of_gates_pct === undefined) return null
  const e = Number(tvb.net_effect_of_gates_pct)
  if (Math.abs(e) < 0.005) return 'Taken and blocked signals moved about the same after cost: the gates are not adding or removing edge.'
  return e > 0
    ? `Taken signals beat blocked ones by ${e.toFixed(3)} points after cost: the gates are letting through the better signals.`
    : `Blocked signals beat taken ones by ${Math.abs(e).toFixed(3)} points after cost: the gates are keeping out the better signals.`
}

/** True when the payload is older than `maxAgeMs` or the last fetch failed (the page then says STALE instead of showing the old verdict as fresh). */
export function isStale(fetchedAt, error, now, maxAgeMs = 180000) {
  return !!error || !fetchedAt || now - fetchedAt > maxAgeMs
}

/** Coverage rows to display, newest last, with shares as text. */
export function coverageRows(params) {
  return ((params && params.coverage) || []).map(r => ({ ...r, pv: fmtShare(r.param_version_share, 0), dq: fmtShare(r.decision_quote_share, 0) }))
}
